import React, { useState, useEffect, useCallback } from "react";
import { Link as RouterLink } from "react-router-dom";
import { styled } from "@mui/system";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Autocomplete from "@mui/lab/Autocomplete";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import employerSvc from "../../services/employer/employerSvc";
import { isTrustedParentMessage } from "../../utils/iframeMessaging";

// Styled Autocomplete component for consistency
const StyledAutocomplete = styled(Autocomplete)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    paddingLeft: theme.spacing(1),
  },
}));

function SearchForms() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // Debug flag - set to true to enable console logs
  const DEBUG_ROUTING = false;

  const debugLog = useCallback((message, data = null) => {
    if (DEBUG_ROUTING) {
      if (data) {
        console.log('SearchForms:', message, data);
      } else {
        console.log('SearchForms:', message);
      }
    }
  }, [DEBUG_ROUTING]);

  // New: State to store parent website
  const [parentWebsite, setParentWebsite] = useState(null);

  // Listen for postMessage from parent for website info
  useEffect(() => {
    function handleMessage(event) {
      debugLog("SearchForms received message:", event.data);
      if (!isTrustedParentMessage(event)) {
        debugLog("Ignoring message from untrusted source:", event.origin);
        return;
      }
      if (event.data && event.data.website) {
        setParentWebsite(event.data.website);
        debugLog("Received parent website via postMessage:", event.data.website);
      } else if (event.data && event.data.type === 'ROUTE_CHANGE' && event.data.website) {
        setParentWebsite(event.data.website);
        debugLog("Received parent website via ROUTE_CHANGE:", event.data.website);
      } else if (event.data && event.data.type === 'WEBSITE_INFO' && event.data.website) {
        setParentWebsite(event.data.website);
        debugLog("Received parent website via WEBSITE_INFO:", event.data.website);
      }
    }
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [debugLog]);

  // Compute websiteName, prefer parentWebsite if present
  const websiteName = React.useMemo(() => {
    if (parentWebsite) {
      switch (parentWebsite) {
        case "region10rams.org":
          return "Region 10 RAMS";
        case "calstrs403bcomply.com":
          return "CALSTRS";
        case "tcgservices.com":
          return "TCG Services";
        case "finpath403b.com":
          return "FinPath 403b";
        // Add more mappings as needed
        default:
          return parentWebsite;
      }
    }
    // Default to TCG Services if no parentWebsite provided
    return "TCG Services";
  }, [parentWebsite]);

  // Local state declarations
  const [loading, setLoading] = useState(true);
  const [employers, setEmployers] = useState([]);
  const [selectedEmployer, setSelectedEmployer] = useState(null);
  const [searchValue, setSearchValue] = useState("");

  // Fetch employers based on website identity
  useEffect(() => {
    // Make API call if we have a parentWebsite OR if we're in standalone mode (localhost)
    // This ensures we make the call for all parent websites, including tcgservices.com
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    if (parentWebsite || isLocalhost) {
      const getEmployers = async () => {
        try {
          setLoading(true);
          debugLog("Making API call with websiteName:", websiteName);
          debugLog("API call parameters:", {
            website: websiteName,
            page: "formSearch",
          });
          
          const response = await employerSvc.getEmployersByWebsite({
            website: websiteName,
            page: "formSearch",
          });
          
          debugLog("API response:", response);
          setEmployers(response);
        } catch (error) {
          console.error("API call error:", error);
        } finally {
          setLoading(false);
        }
      };
      getEmployers();
    }
  }, [websiteName, parentWebsite, debugLog]);

  return (
    <Container
      component="main"
      maxWidth="md"
      sx={{ mt: isMobile ? 2 : 8, mb: isMobile ? 3 : 5 }}
    >
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 300,
          }}
        >
          <CircularProgress size={40} />
        </Box>
      ) : (
        <Box>
          {/* Search Card */}
          <Card sx={{ p: isMobile ? 2 : 3, boxShadow: "none" }}>
            <CardContent>
              <Box sx={{ textAlign: "center", mb: 2 }}>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", color: "#297ac2" }}
                >
                  Find Your Retirement Plan Information
                </Typography>
                <Typography variant="body2" sx={{ color: "gray", mt: 1 }}>
                  (Type at least 3 characters)
                </Typography>
              </Box>
                <Grid
                  container
                  spacing={2}
                  alignItems="center"
                  justifyContent="center" // Center horizontally
                  sx={{ textAlign: "center" }} // Ensure text aligns center
                >
                  <Grid item xs={12} sm={6} md={5}>
                    <StyledAutocomplete
                      id="clientSearchBar"
                      value={selectedEmployer}
                      inputValue={searchValue}
                      open={searchValue.length >= 3 && selectedEmployer === null}
                      forcePopupIcon={false}
                      noOptionsText="No employers found."
                      getOptionLabel={(option) => option.employerName || ""}
                      onChange={(_, employer) => setSelectedEmployer(employer)}
                      onInputChange={(_, value) => setSearchValue(value)}
                      options={employers}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Employer Name"
                          fullWidth
                          size={isMobile ? "small" : "medium"}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3} md={2}>
                    <Button
                      fullWidth
                      variant="contained"
                      component={RouterLink}
                      to={
                        selectedEmployer
                          ? `/search-forms/${selectedEmployer.employerIdForURL}`
                          : ""
                      }
                      disabled={!selectedEmployer}
                      sx={{
                        fontWeight: 500,
                        backgroundColor: "#041E42",
                        "&:hover": { backgroundColor: "#041E42" },
                        fontSize: isMobile ? "0.8rem" : "1rem",
                        padding: isMobile ? "6px 10px" : "8px 16px",
                      }}
                    >
                      Search
                    </Button>
                  </Grid>
                </Grid>

            </CardContent>
          </Card>

          {/* RMD Form Download Section */}
          <Box sx={{ mt: 4, textAlign: "center" }}>
            <Typography
              variant="body1"
              sx={{ color: "#666", fontSize: isMobile ? "16px" : "18px" }}
            >
              Looking for an RMD form?
            </Typography>
            <Button
              variant="contained"
              onClick={() => {
                const url = 'https://tcgservices.com/wp-content/uploads/2021/12/TCG-RMD-Form.pdf';
                if (window.triggerExternalVendorLink) {
                  window.triggerExternalVendorLink(url);
                } else {
                  // Fallback: create a temporary anchor and click it
                  const link = document.createElement('a');
                  link.href = url;
                  link.target = '_blank';
                  link.rel = 'noopener noreferrer';
                  link.style.display = 'none';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }
              }}
              sx={{
                mt: 2,
                backgroundColor: "#09497a",
                boxShadow: "none",
                borderRadius: "1px",
                "&:hover": { backgroundColor: "#062D4C" },
                fontSize: isMobile ? "0.8rem" : "1rem",
                padding: isMobile ? "6px 10px" : "8px 16px",
                color: "white",
                textDecoration: "none"
              }}
            >
              Download now
            </Button>
          </Box>
        </Box>
      )}
    </Container>
  );
}

export default SearchForms;
